import { useState, useEffect, useCallback } from 'react';
import { getLeads, updateLead, deleteLead } from '../../lib/api';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Search, ChevronLeft, ChevronRight, Trash2, Edit } from 'lucide-react';

const CRM_STAGES = [
  { value: 'new_lead', label: 'New Lead', color: 'bg-zinc-800 text-zinc-300' },
  { value: 'contacted', label: 'Contacted', color: 'bg-blue-500/10 text-blue-400' },
  { value: 'demo_scheduled', label: 'Demo Scheduled', color: 'bg-indigo-500/10 text-indigo-400' },
  { value: 'demo_completed', label: 'Demo Completed', color: 'bg-purple-500/10 text-purple-400' },
  { value: 'follow_up', label: 'Follow Up', color: 'bg-yellow-500/10 text-yellow-400' },
  { value: 'proposal', label: 'Proposal', color: 'bg-orange-500/10 text-orange-400' },
  { value: 'contract', label: 'Contract', color: 'bg-teal-500/10 text-teal-400' },
  { value: 'closed_won', label: 'Closed Won', color: 'bg-emerald-500/10 text-emerald-400' },
  { value: 'closed_lost', label: 'Closed Lost', color: 'bg-red-500/10 text-red-400' },
];

function StageBadge({ stage }) {
  const s = CRM_STAGES.find((c) => c.value === stage) || CRM_STAGES[0];
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>;
}

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [stageFilter, setStageFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [editLead, setEditLead] = useState(null);
  const [editForm, setEditForm] = useState({});
  const limit = 20;

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * limit, limit };
      if (stageFilter !== 'all') params.stage = stageFilter;
      const res = await getLeads(params);
      setLeads(res.data.leads);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, stageFilter]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    await deleteLead(id);
    fetchLeads();
  };

  const openEdit = (lead) => {
    setEditLead(lead);
    setEditForm({
      name: lead.name || '',
      company: lead.company || '',
      crm_stage: lead.crm_stage,
      deal_value: lead.deal_value || 0,
      notes: lead.notes || '',
    });
  };

  const handleUpdateLead = async () => {
    await updateLead(editLead.id, editForm);
    setEditLead(null);
    fetchLeads();
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-100">Leads</h1>
          <p className="text-sm text-zinc-500 mt-1">{total} total leads</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Select value={stageFilter} onValueChange={(v) => { setStageFilter(v); setPage(0); }}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by stage" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stages</SelectItem>
            {CRM_STAGES.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-800/50 text-left">
                <th className="px-4 py-3 font-medium text-zinc-500">Email</th>
                <th className="px-4 py-3 font-medium text-zinc-500">Name</th>
                <th className="px-4 py-3 font-medium text-zinc-500">Company</th>
                <th className="px-4 py-3 font-medium text-zinc-500">Stage</th>
                <th className="px-4 py-3 font-medium text-zinc-500">Deal Value</th>
                <th className="px-4 py-3 font-medium text-zinc-500">Email Sent</th>
                <th className="px-4 py-3 font-medium text-zinc-500">Date</th>
                <th className="px-4 py-3 font-medium text-zinc-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-8 text-zinc-400">Loading...</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-8 text-zinc-400">No leads found</td></tr>
              ) : (
                leads.map((lead) => (
                  <tr key={lead.id} className="border-b border-zinc-800/50 hover:bg-zinc-950/50">
                    <td className="px-4 py-3 font-medium text-zinc-100">{lead.email}</td>
                    <td className="px-4 py-3 text-zinc-400">{lead.name || '-'}</td>
                    <td className="px-4 py-3 text-zinc-400">{lead.company || '-'}</td>
                    <td className="px-4 py-3"><StageBadge stage={lead.crm_stage} /></td>
                    <td className="px-4 py-3 text-zinc-400">${(lead.deal_value || 0).toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <Badge variant={lead.email_sent ? 'default' : 'secondary'}>
                        {lead.email_sent ? 'Sent' : 'Pending'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(lead)}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(lead.id)} className="text-red-500 hover:text-red-400">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800/50">
            <p className="text-xs text-zinc-400">Page {page + 1} of {totalPages}</p>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editLead} onOpenChange={(o) => !o && setEditLead(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium text-zinc-300">Name</label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-300">Company</label>
              <Input value={editForm.company} onChange={(e) => setEditForm({ ...editForm, company: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-300">CRM Stage</label>
              <Select value={editForm.crm_stage} onValueChange={(v) => setEditForm({ ...editForm, crm_stage: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CRM_STAGES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-300">Deal Value ($)</label>
              <Input type="number" value={editForm.deal_value} onChange={(e) => setEditForm({ ...editForm, deal_value: parseFloat(e.target.value) || 0 })} />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-300">Notes</label>
              <textarea
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                className="w-full border border-zinc-800 rounded-lg p-2 text-sm min-h-[80px] focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditLead(null)}>Cancel</Button>
              <Button onClick={handleUpdateLead}>Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
